import React, { useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import {
  FaArchive,
  FaCheck,
  FaClock,
  FaClone,
  FaCompressAlt,
  FaEllipsisH,
  FaExpandAlt,
  FaExternalLinkAlt,
  FaPaperclip,
  FaRegCommentDots,
  FaTrash,
} from "react-icons/fa";

import {
  cx,
  formatDateFR,
  getChecklistProgress,
  getDueDateState,
  getPriorityBadge,
  getStatusBadge,
  isTaskLate,
  normalizeTaskStatus,
  stripHtml,
} from "../utils/tasksKanbanUtils";

function getCompactStatusTone(status) {
  const normalized = normalizeTaskStatus(status);

  if (normalized === "Terminee") {
    return {
      cardClass: "border-emerald-200 bg-emerald-50 hover:border-emerald-300",
      accentColor: "#22c55e",
      pillClass: "bg-emerald-100 text-emerald-700",
    };
  }

  if (normalized === "En cours") {
    return {
      cardClass: "border-orange-200 bg-orange-50 hover:border-orange-300",
      accentColor: "#f97316",
      pillClass: "bg-orange-100 text-orange-700",
    };
  }

  if (normalized === "Urgent") {
    return {
      cardClass: "border-violet-200 bg-violet-50 hover:border-violet-300",
      accentColor: "#8b5cf6",
      pillClass: "bg-violet-100 text-violet-700",
    };
  }

  return {
    cardClass: "border-yellow-200 bg-yellow-50 hover:border-yellow-300",
    accentColor: "#facc15",
    pillClass: "bg-yellow-100 text-yellow-700",
  };
}

export default function TaskKanbanCard({
  task,
  index,
  expanded,
  onToggleExpanded,
  onOpenTask,
  onToggleDone,
  onDuplicate,
  onArchive,
  onDelete,
}) {
  const late = isTaskLate(task);
  const dueState = getDueDateState(task);
  const status = getStatusBadge(task.statut);
  const priority = getPriorityBadge(task.priority);
  const checklist = getChecklistProgress(task);
  const contentPreview = stripHtml(task.contenu || "");

  const labelColor = task.label_color || "#38bdf8";
  const compactTone = getCompactStatusTone(task.statut);
  const accentColor = expanded ? labelColor : compactTone.accentColor;

  return (
    <Draggable draggableId={`task-${task.id}`} index={index}>
      {(provided, snapshot) => (
        <article
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cx(
            "group relative overflow-hidden rounded-2xl border shadow-sm ring-1 ring-black/[0.03] transition hover:-translate-y-0.5 hover:shadow-xl",
            expanded
              ? late
                ? "border-red-200 bg-white hover:border-red-300"
                : "border-slate-200 bg-white hover:border-sky-300"
              : compactTone.cardClass,
            snapshot.isDragging && "rotate-[1deg] shadow-2xl",
          )}
        >
          <div
            className="absolute inset-y-0 left-0 w-1.5"
            style={{ backgroundColor: accentColor }}
          />

          {!expanded ? (
            <CompactTaskCard
              task={task}
              status={status}
              priority={priority}
              late={late}
              dueState={dueState}
              compactTone={compactTone}
              onOpenTask={onOpenTask}
              onToggleExpanded={onToggleExpanded}
            />
          ) : (
            <ExpandedTaskCard
              task={task}
              status={status}
              priority={priority}
              late={late}
              dueState={dueState}
              checklist={checklist}
              contentPreview={contentPreview}
              labelColor={labelColor}
              onOpenTask={onOpenTask}
              onToggleExpanded={onToggleExpanded}
              onToggleDone={onToggleDone}
              onDuplicate={onDuplicate}
              onArchive={onArchive}
              onDelete={onDelete}
            />
          )}
        </article>
      )}
    </Draggable>
  );
}

function CompactTaskCard({
  task,
  status,
  priority,
  late,
  dueState,
  compactTone,
  onOpenTask,
  onToggleExpanded,
}) {
  return (
    <div className="pl-2">
      <div className="p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span
              className={cx(
                "max-w-[120px] truncate rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
                compactTone.pillClass,
              )}
            >
              {status.label}
            </span>

            {task.categorie && (
              <span className="max-w-[105px] truncate rounded-full bg-white/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                {task.categorie}
              </span>
            )}
          </div>

          <span
            className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${priority.className}`}
          >
            {priority.label}
          </span>
        </div>

        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenTask?.(task);
            }}
            className="min-w-0 text-left"
          >
            <h3 className="line-clamp-2 text-sm font-bold leading-snug text-[#172b4d]">
              {task.titre || "Tâche sans titre"}
            </h3>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded?.(task.id);
            }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white/75 text-slate-500 shadow-sm transition hover:bg-white hover:text-sky-700"
            title="Agrandir cette carte"
          >
            <FaExpandAlt className="text-xs" />
          </button>
        </div>

        {task.association?.nom_asso && (
          <div className="mt-2 truncate text-xs font-bold text-slate-600">
            {task.association.nom_asso}
          </div>
        )}

        {task.product_app?.nom_app && (
          <div className="mt-0.5 truncate text-[11px] font-medium text-slate-500">
            {task.product_app.nom_app}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between gap-2">
          <span
            className={cx(
              "inline-flex min-w-0 items-center gap-1.5 rounded-xl px-2 py-1 text-[11px] font-bold",
              late ? "bg-red-100 text-red-700" : "bg-white/75 text-slate-600",
            )}
          >
            <FaClock className="shrink-0" />
            <span className="truncate">
              {task.due_date ? formatDateFR(task.due_date) : "Sans échéance"}
            </span>
          </span>

          <span
            className={cx(
              "shrink-0 rounded-full px-2 py-1 text-[10px] font-bold",
              dueState.tone === "danger" && "bg-red-600 text-white",
              dueState.tone === "warning" && "bg-amber-100 text-amber-700",
              dueState.tone === "safe" && "bg-emerald-100 text-emerald-700",
              dueState.tone === "done" && "bg-emerald-100 text-emerald-700",
              dueState.tone === "muted" && "bg-white/75 text-slate-500",
            )}
          >
            {dueState.label}
          </span>
        </div>
      </div>
    </div>
  );
}

function ExpandedTaskCard({
  task,
  status,
  priority,
  late,
  dueState,
  checklist,
  contentPreview,
  labelColor,
  onOpenTask,
  onToggleExpanded,
  onToggleDone,
  onDuplicate,
  onArchive,
  onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const labels = Array.isArray(task.labels_json) ? task.labels_json : [];
  const attachmentsCount =
    task.attachments_count ||
    (Array.isArray(task.attachments_json) ? task.attachments_json.length : 0);

  const commentsCount =
    task.comments_count ||
    (Array.isArray(task.comments_json) ? task.comments_json.length : 0);

  return (
    <div className="pl-2">
      {task.cover_url && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenTask?.(task);
          }}
          className="block h-32 w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200"
        >
          <img
            src={task.cover_url}
            alt={task.titre}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </button>
      )}

      <div className="p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span
              className="h-2 w-10 rounded-full"
              style={{ backgroundColor: labelColor }}
            />

            {labels.slice(0, 3).map((label, labelIndex) => (
              <span
                key={`${task.id}-label-${labelIndex}`}
                className="max-w-[110px] truncate rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600"
              >
                {label.label || label.name || label}
              </span>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenTask?.(task);
              }}
              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-blue-100 hover:text-blue-700"
              title="Ouvrir la fiche"
            >
              <FaExternalLinkAlt className="text-xs" />
            </button>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded?.(task.id);
              }}
              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-sky-100 hover:text-sky-700"
              title="Réduire cette carte"
            >
              <FaCompressAlt className="text-xs" />
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuOpen((current) => !current);
                }}
                className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
              >
                <FaEllipsisH />
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-9 z-30 w-52 rounded-2xl border border-slate-200 bg-white p-2 text-sm shadow-2xl">
                  <MenuButton
                    icon={FaCheck}
                    label="Terminer / rouvrir"
                    onClick={() => {
                      setMenuOpen(false);
                      onToggleDone?.(task);
                    }}
                  />
                  <MenuButton
                    icon={FaClone}
                    label="Dupliquer"
                    onClick={() => {
                      setMenuOpen(false);
                      onDuplicate?.(task);
                    }}
                  />
                  <MenuButton
                    icon={FaArchive}
                    label="Archiver"
                    onClick={() => {
                      setMenuOpen(false);
                      onArchive?.(task);
                    }}
                  />
                  <MenuButton
                    icon={FaTrash}
                    label="Supprimer"
                    danger
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete?.(task);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenTask?.(task);
          }}
          className="mb-2 block min-w-0 text-left"
        >
          <h3 className="line-clamp-3 text-sm font-bold leading-snug text-[#172b4d]">
            {task.titre || "Tâche sans titre"}
          </h3>
        </button>

        {contentPreview && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenTask?.(task);
            }}
            className="mb-2 line-clamp-3 rounded-xl bg-slate-50 px-3 py-2 text-left text-xs leading-relaxed text-slate-500"
          >
            {contentPreview}
          </button>
        )}

        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span
            className={cx(
              "rounded-full border px-2 py-1 text-[10px] font-bold",
              status.className,
            )}
          >
            {status.label}
          </span>

          <span
            className={cx(
              "rounded-full px-2 py-1 text-[10px] font-bold",
              priority.className,
            )}
          >
            {priority.label}
          </span>

          {task.categorie && (
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
              {task.categorie}
            </span>
          )}
        </div>

        {(task.association?.nom_asso || task.product_app?.nom_app) && (
          <div className="mb-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {task.association?.nom_asso && (
              <div className="truncate font-bold text-slate-700">
                {task.association.nom_asso}
              </div>
            )}

            {task.product_app?.nom_app && (
              <div className="mt-0.5 truncate text-slate-500">
                {task.product_app.nom_app}
              </div>
            )}
          </div>
        )}

        {checklist.total > 0 && (
          <div className="mb-2">
            <div className="mb-1 flex items-center justify-between text-[11px] font-bold text-slate-500">
              <span>Checklist</span>
              <span>
                {checklist.done}/{checklist.total}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={cx(
                  "h-full rounded-full transition-all",
                  checklist.done === checklist.total
                    ? "bg-emerald-500"
                    : "bg-sky-500",
                )}
                style={{ width: `${checklist.percent}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span
            className={cx(
              "inline-flex items-center gap-1 rounded-xl px-2 py-1 font-bold",
              late ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600",
            )}
          >
            <FaClock />
            {task.due_date ? formatDateFR(task.due_date) : "Sans échéance"}
          </span>

          <span
            className={cx(
              "inline-flex items-center rounded-xl px-2 py-1 text-[11px] font-bold",
              dueState.tone === "danger" && "bg-red-600 text-white",
              dueState.tone === "warning" && "bg-amber-100 text-amber-700",
              dueState.tone === "safe" && "bg-emerald-100 text-emerald-700",
              dueState.tone === "done" && "bg-emerald-100 text-emerald-700",
              dueState.tone === "muted" && "bg-slate-100 text-slate-500",
            )}
          >
            {dueState.label}
          </span>

          {commentsCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <FaRegCommentDots />
              {commentsCount}
            </span>
          )}

          {attachmentsCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <FaPaperclip />
              {attachmentsCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuButton({ icon: Icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className={cx(
        "flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left font-bold transition",
        danger
          ? "text-red-600 hover:bg-red-50"
          : "text-slate-700 hover:bg-slate-100",
      )}
    >
      <Icon />
      {label}
    </button>
  );
}